<script setup lang="ts">
    import {useI18n} from 'vue-i18n';
    import useCpBem from '~/composables/useCpBem';
    import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';
    import CpText from '~/components/cp/CpText/CpText.vue';
    import {getEtaMessage, getEtaMessageModal} from '~/utils/etaUtils';
    import type {IEtaInfo} from '~/types';
    import CpAlert from '~/components/cp/CpAlert/CpAlert.vue';
    import CpButton from '~/components/cp/CpButton/CpButton.vue';
    import CpModal from '~/components/cp/CpModal/CpModal.vue';
    import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
    import {useCpUser} from '~/composables/useCpUser';
    // App component reference ProductArrival

    const {t} = useI18n();
    const {b, e} = useCpBem('cp-section-pdp-eta');

    const props = defineProps<{
      eta: IEtaInfo,
      isEsd?: boolean
    }>();
    const displayMessage = getEtaMessage(props.eta);
    const displayMessageModal = getEtaMessageModal(props.eta);
    const showEtaDialog = ref(false);
    const user = useCpUser();
    const loggedUser = user.isAuthenticated.value;
    const formmatedMessage = '*' + getEtaMessage(props.eta)?.replace(/\*/g, '') + '*';

    function openModal() {
      showEtaDialog.value = true;
    }
</script>

<template>
    <div :class="b" @click="openModal">
        <CpIconStandard icon="calendar_today" :class="e('icon')" type="primary" />
        <CpTextParser v-if="!isEsd && displayMessage" :class="e('eta')" :text="displayMessage" variant="caption" />
        <CpText v-else variant="body-bold" type="primary" data-testid="pdp-esd-eta">
            {{ t('esd') }}
        </CpText>
        <CpModal :show="showEtaDialog" @close-modal="showEtaDialog=false">
            <div :class="e`background`">
                <CpSectionHeader
                    :class="e`full-width`"
                    :title="t('eta')"
                    type="h2"
                    button="close"
                    @header-action="showEtaDialog = false"
                />
                <div :class="e`body`">
                    <CpIconStandard icon="local_shipping" type="primary" kind="extra-large" />
                    <CpTextParser
                        :text="formmatedMessage ? formmatedMessage : ''"
                    />
                    <CpTextParser
                        variant="body"
                        :text="t('afterPay')"
                    />
                    <CpTextParser
                        v-if="loggedUser && displayMessageModal"
                        :text="displayMessageModal"
                    />
                    <CpTextParser
                        v-if="!loggedUser"
                        variant="body"
                        :text="t('afterShipping')"
                    />
                    <CpAlert
                        v-if="!loggedUser"
                        kind="info"
                        :close-icon="false"
                    >
                        <CpText variant="body-regular">
                            {{ t('alertMessage') }}
                        </CpText>
                    </CpAlert>
                    <CpText variant="caption-regular">
                        {{ t('shippingCost') }}
                    </CpText>
                </div>
                <div :class="e`button-container`">
                    <CpButton
                        :label="t('back')"
                        hollow
                        kind="label"
                        full-width
                        @click="showEtaDialog = false"
                    />
                </div>
            </div>
        </CpModal>
    </div>
</template>

<style scoped lang="scss">
  .cp-section-pdp-eta {
    display: flex;
    align-items: center;
    padding: $cp-gutter-small $cp-gutter;
    border-bottom: 1px solid var(--color-neutral-extra-light);
    &__icon {
      margin-right: $cp-gutter-xsmall;
    }
    &__eta {
      flex-grow: 1;
    }
    &__background {
      display: flex;
      flex-direction: column;
      background-color: var(--color-white);
      overflow: hidden;
      border-top-right-radius: $cp-gutter-small;
      border-top-left-radius: $cp-gutter-small;
      line-height: 0;
    }
    &__body {
      padding: $cp-gutter;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: $cp-gutter;
      overflow-x: hidden;
      overflow-y: scroll;
    }
    &__body::-webkit-scrollbar {
      width: 0;
    }
    &__button-container {
      padding: $cp-gutter;
      border-top: $cp-border-standard;
    }
  }
</style>
<i18n locale="es-US" lang="yaml">
  esd: 'Entrega inmediata al procesar tu pedido'
  eta: 'Fecha estimada de entrega'
  estimatedDate: 'Recibelo entre el'
  afterShipping: 'El tiempo de entrega a partir del envío generalmente tarda *de 1 a 5 días hábiles.*'
  alertMessage: 'Inicia sesión y elige una dirección para ver el dato exacto. El tiempo de entrega puede variar dependiendo de la región a la que se envía.'
  afterPay: 'Después de realizar tu pago, necesitamos *24 horas* para procesar tu pedido y enviarlo.'
  shippingCost: 'El costo de envío mostrado en el producto aplica para toda la República Mexicana. '
  back: 'Regresar'
</i18n>
